import React, { useState } from "react";
import { FileText, FileType2, Languages, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Panel, Btn, Overline } from "./ui";
import { exportJudicialDocx, exportJudicialPdf, exportJudicialPdfZh, verifyUrlFor } from "../lib/reportExport";
import { useI18n } from "../lib/i18n";

export function ReportExportPanel({ report, summary, evidence, matter, officer, clientHash, onEnsureReport }) {
  const { t, lang } = useI18n();
  const [busy, setBusy] = useState(null);

  const run = async (kind) => {
    setBusy(kind);
    try {
      let rep = report;
      if (!rep) {
        toast.info(t("exp.needReport"));
        rep = await onEnsureReport();
      }
      if (!rep) {
        toast.error(t("exp.failed"));
        return;
      }
      const payload = { report: rep, summary, evidence, matter, officer, clientHash, lang };
      const filename =
        kind === "docx"
          ? await exportJudicialDocx(payload)
          : kind === "pdfzh"
          ? await exportJudicialPdfZh(payload)
          : await exportJudicialPdf(payload);
      toast.success(t("exp.done", { f: filename }));
    } catch {
      toast.error(t("exp.failed"));
    } finally {
      setBusy(null);
    }
  };

  const verifyUrl = verifyUrlFor(report);

  return (
    <Panel icon={FileText} title={t("exp.title")} subtitle={t("exp.sub")} accent="amber" testid="report-export-panel">
      <div className="space-y-3">
        <div className="rounded-lg border border-edge well px-4 py-3">
          <Overline>{matter?.matter_id || "MATTER"}</Overline>
          <p className="mt-1 text-[13px] text-slate-300">{matter?.[`name_${lang}`] || matter?.name_en || "—"}</p>
          <p className="mt-1 break-all font-mono text-[10px] text-aegis-emerald">
            {clientHash || evidence?.[0]?.sha256 || "—"}
          </p>
          {verifyUrl && <p className="mt-1 break-all font-mono text-[10px] text-aegis-cyan">{verifyUrl}</p>}
        </div>

        <Btn variant="amber" className="w-full py-3" disabled={!!busy} onClick={() => run("docx")} testid="export-docx-btn">
          {busy === "docx" ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />}
          {busy === "docx" ? t("exp.building") : t("exp.docx")}
        </Btn>
        <Btn variant="emerald" className="w-full py-3" disabled={!!busy} onClick={() => run("pdfzh")} testid="export-pdf-zh-btn">
          {busy === "pdfzh" ? <Loader2 size={16} className="animate-spin" /> : <Languages size={16} />}
          {busy === "pdfzh" ? t("exp.building") : t("exp.pdfzh")}
        </Btn>
        <Btn variant="cyan" className="w-full py-3" disabled={!!busy} onClick={() => run("pdf")} testid="export-pdf-btn">
          {busy === "pdf" ? <Loader2 size={16} className="animate-spin" /> : <FileType2 size={16} />}
          {busy === "pdf" ? t("exp.building") : t("exp.pdf")}
        </Btn>
        <p className="text-[11px] leading-relaxed text-slate-400">{t("exp.note")}</p>
      </div>
    </Panel>
  );
}
